import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { DatabaseService } from '../../services/database.service';

interface DiffDescriptor {
  index?: number;
  key: string;
  identifier?: string;
}

@Component({
  selector: 'app-compare-view',
  standalone: false,
  templateUrl: './compare-view.component.html',
  styleUrl: './compare-view.component.scss'
})
export class CompareViewComponent implements OnInit, OnChanges {
  @Input() highlightData?: any;
  @Input() inventoryDiffCount = 0;
  @Input() inventoryExpanded = false;
  @Input() inventoryLoading = false;

  @Input() interfaceDiffCount = 0;
  @Input() interfaceExpanded = false;
  @Input() interfaceLoading = false;
  @Input()
  set interfaceData(value: any[] | null) {
    this.selectedInterface = value ?? null;
    this.interfaceStart = 0;
    if (value) {
      this.interfacesCollapsed = false;
    }
  }

  @Input() dnsDiffCount = 0;
  @Input() dnsExpanded = false;
  @Input() dnsLoading = false;
  @Input()
  set dnsData(value: any[] | null) {
    this.selectedDns = value ?? null;
    this.dnsStart = 0;
    if (value) {
      this.dnsCollapsed = false;
    }
  }

  @Output() outputEvent = new EventEmitter<any>(); // Child sends data out

  allInventory: any;
  selectedInventory: any;
  selectedInterface: any[] | null = null;
  selectedDns: any[] | null = null;

  inventoryHighlightKeys: string[] = [];
  interfaceHighlightDescriptors: DiffDescriptor[] = [];
  dnsHighlightDescriptors: DiffDescriptor[] = [];

  // Collapse state
  interfacesCollapsed = false;
  dnsCollapsed = false;

  // Virtual scroll window sizes
  interfaceWindowSize = 25;
  dnsWindowSize = 25;
  interfaceStart = 0;
  dnsStart = 0;

  constructor(private _db: DatabaseService) { }

  ngOnInit() {
    this._db.getAllInventory().subscribe((res) => {
      // console.log("Testing get all inventory", res);
      this.allInventory = res;
      // this._db.getInventoryBySerial(res[0].serialNumber).subscribe((res) => {
      //   // console.log("Testing get inventory by serial", res);
      // });
      // this._db.getInterfaceBySerial(res[0].serialNumber).subscribe((res) => {
      //   // console.log("Testing get interface by serial", res);
      // });
      // this._db.getDnsBySerial(res[0].serialNumber).subscribe((res) => {
      //   // console.log("Testing get dns by serial", res);
      // });
    });
  }
  
  ngOnChanges(changes: SimpleChanges) {
    if ('highlightData' in changes) {
      this.inventoryHighlightKeys = [];
      this.interfaceHighlightDescriptors = [];
      this.dnsHighlightDescriptors = [];

      if (this.highlightData?.length) {
        for (const section of this.highlightData) {
          switch (section.type) {
            case 'inventory':
              for (const data of section.data) {
                this.inventoryHighlightKeys.push(data.key);
              }
              break;
            case 'interface':
              for (const data of section.data) {
                this.interfaceHighlightDescriptors.push({ index: data.index, key: data.key, identifier: data.identifier });
              }
              break;
            case 'dns':
              for (const data of section.data) {
                this.dnsHighlightDescriptors.push({ index: data.index, key: data.key, identifier: data.identifier });
              }
              break;
            default:
              console.error('Unknown highlight type:', section.type);
          }
        }
      }
    }
  }
  
  onInventoryChange(selected: any) {
    this.selectedInventory = selected;
    this.selectedInterface = null;
    this.selectedDns = null;
    this.interfaceStart = 0;
    this.dnsStart = 0;
    this.inventoryHighlightKeys = [];
    this.interfaceHighlightDescriptors = [];
    this.dnsHighlightDescriptors = [];

    this.sendDataToParent({
      inventory: selected
    });
  }

  sendDataToParent(data: any) {
    this.outputEvent.emit(data);
  }

  isInterfaceHighlighted(index: number, key: string, record: any): boolean {
    return this.interfaceHighlightDescriptors.some(descriptor => {
      if (descriptor.key !== key) {
        return false;
      }

      let matches = false;

      if (typeof descriptor.index === 'number') {
        matches = descriptor.index === index;
      }

      if (!matches && descriptor.identifier) {
        matches = this.matchesIdentifier(record, descriptor.identifier);
      }

      return matches;
    });
  }

  isDnsHighlighted(index: number, key: string, record: any): boolean {
    return this.dnsHighlightDescriptors.some(descriptor => {
      if (descriptor.key !== key) {
        return false;
      }

      let matches = false;

      if (typeof descriptor.index === 'number') {
        matches = descriptor.index === index;
      }

      if (!matches && descriptor.identifier) {
        matches = this.matchesIdentifier(record, descriptor.identifier);
      }

      return matches;
    });
  }

  // Toggle helpers
  toggleInterfacesCollapsed() { this.interfacesCollapsed = !this.interfacesCollapsed; }
  toggleDnsCollapsed() { this.dnsCollapsed = !this.dnsCollapsed; }

  // Sliced data for virtual scrolling
  getVisibleInterfaces() {
    if (!this.selectedInterface) return [];
    return this.selectedInterface.slice(this.interfaceStart, this.interfaceStart + this.interfaceWindowSize);
  }
  getVisibleDns() {
    if (!this.selectedDns) return [];
    return this.selectedDns.slice(this.dnsStart, this.dnsStart + this.dnsWindowSize);
  }
  loadMoreInterfaces() {
    if (this.selectedInterface && this.interfaceStart + this.interfaceWindowSize < this.selectedInterface.length) {
      this.interfaceStart += this.interfaceWindowSize;
    }
  }
  loadMoreDns() {
    if (this.selectedDns && this.dnsStart + this.dnsWindowSize < this.selectedDns.length) {
      this.dnsStart += this.dnsWindowSize;
    }
  }

  getInterfaceDiffCount(): number {
    return this.interfaceDiffCount;
  }
  getDnsDiffCount(): number {
    return this.dnsDiffCount;
  }

  trackByIndex(idx: number, _item: any) { return idx; }

  private matchesIdentifier(record: any, identifier: string): boolean {
    if (!record) {
      return false;
    }

    const candidateKeys = ['id', 'identifier', 'name', 'interfaceName', 'dnsServer', 'serialNumber'];
    return candidateKeys.some(key => record[key] && String(record[key]) === identifier);
  }
}
